import { useState } from "react";
import { ScanSearch } from "lucide-react";
import { BUSINESS_TYPES, CHALLENGES, WA_MESSAGES } from "../data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { waLink, composeScanMessage } from "../lib/whatsapp";

const PHONE_RE = /^[+0-9][0-9\s\-()]{6,19}$/;

const inputCls =
    "w-full rounded-xl border border-brand-brown/15 bg-white px-4 py-3 text-sm text-brand-brown placeholder:text-brand-muted/60 transition-colors duration-200 focus:border-brand-orange focus:outline-none";

export const ScanForm = () => {
    const [form, setForm] = useState({ name: "", business_name: "", phone: "", business_type: "", challenge: "", message: "", website: "" });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({ state: "idle", note: "" });

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const validate = () => {
        const errs = {};
        if (form.name.trim().length < 2) errs.name = "Please enter your name.";
        if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) errs.phone = "Please enter a valid phone / WhatsApp number.";
        if (form.message.length > 1000) errs.message = "Please keep the message under 1000 characters.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const submit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        // Honeypot filled in — a bot. Pretend success, open nothing.
        if (form.website) {
            setStatus({ state: "sent", note: "Thank you — your details have been received." });
            return;
        }
        window.open(waLink(composeScanMessage(form)), "_blank", "noopener,noreferrer");
        setStatus({ state: "sent", note: "WhatsApp should now open with your message pre-filled. If it didn't, use the direct link below." });
    };

    return (
        <section id="scan" className="girder-grid bg-white py-16 sm:py-24 lg:py-32" data-testid="scan-section">
            <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:px-16">
                <div>
                    <SectionHeading
                        chapter="08"
                        kicker="Business Scan"
                        title="Let's find the leaks in your customer journey."
                        lead="Tell us a little about your business. We'll look at how customers find you, contact you, book you, review you and come back — and show you exactly where they're slipping away."
                        testid="scan-heading"
                    />
                    <Reveal delay={0.15}>
                        <ul className="mt-8 space-y-3">
                            {["Takes under a minute to request", "Reviewed by a human, not a bot", "You get findings and clear next steps"].map((point) => (
                                <li key={point} className="flex items-center gap-3 text-sm font-medium text-brand-brown">
                                    <ScanSearch size={16} className="shrink-0 text-brand-orange" aria-hidden="true" />
                                    {point}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-8 text-sm text-brand-muted">
                            Prefer to skip the form?{" "}
                            <a
                                href={waLink(WA_MESSAGES.scan)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="scan-direct-whatsapp-link"
                                className="font-semibold text-brand-orange underline decoration-brand-orange/40 underline-offset-4 transition-colors duration-200 hover:text-brand-orange-dark"
                            >
                                Message us directly on WhatsApp
                            </a>
                            .
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <form
                        onSubmit={submit}
                        noValidate
                        data-testid="scan-form"
                        className="rounded-2xl border border-brand-brown/10 bg-brand-paper p-7 shadow-[0_24px_48px_-24px_rgba(53,24,13,0.2)] sm:p-9"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="scan-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-brown">
                                    Your name *
                                </label>
                                <input
                                    id="scan-name"
                                    data-testid="scan-name-input"
                                    type="text"
                                    value={form.name}
                                    onChange={set("name")}
                                    maxLength={80}
                                    autoComplete="name"
                                    className={inputCls}
                                    aria-invalid={!!errors.name}
                                    aria-describedby={errors.name ? "scan-name-error" : undefined}
                                />
                                {errors.name && <p id="scan-name-error" data-testid="scan-name-error" className="mt-1.5 text-xs font-medium text-brand-orange-dark">{errors.name}</p>}
                            </div>
                            <div>
                                <label htmlFor="scan-business" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-brown">
                                    Business name
                                </label>
                                <input
                                    id="scan-business"
                                    data-testid="scan-business-input"
                                    type="text"
                                    value={form.business_name}
                                    onChange={set("business_name")}
                                    maxLength={120}
                                    autoComplete="organization"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="scan-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-brown">
                                    Phone / WhatsApp
                                </label>
                                <input
                                    id="scan-phone"
                                    data-testid="scan-phone-input"
                                    type="tel"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    maxLength={20}
                                    autoComplete="tel"
                                    placeholder="+91 ..."
                                    className={inputCls}
                                    aria-invalid={!!errors.phone}
                                    aria-describedby={errors.phone ? "scan-phone-error" : undefined}
                                />
                                {errors.phone && <p id="scan-phone-error" data-testid="scan-phone-error" className="mt-1.5 text-xs font-medium text-brand-orange-dark">{errors.phone}</p>}
                            </div>
                            <div>
                                <label htmlFor="scan-type" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-brown">
                                    Business type
                                </label>
                                <select
                                    id="scan-type"
                                    data-testid="scan-type-select"
                                    value={form.business_type}
                                    onChange={set("business_type")}
                                    className={inputCls}
                                >
                                    <option value="">Select…</option>
                                    {BUSINESS_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="scan-challenge" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-brown">
                                    Biggest challenge right now
                                </label>
                                <select
                                    id="scan-challenge"
                                    data-testid="scan-challenge-select"
                                    value={form.challenge}
                                    onChange={set("challenge")}
                                    className={inputCls}
                                >
                                    <option value="">Select…</option>
                                    {CHALLENGES.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="scan-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-brown">
                                    Anything else? <span className="font-normal normal-case text-brand-muted">(optional)</span>
                                </label>
                                <textarea
                                    id="scan-message"
                                    data-testid="scan-message-input"
                                    value={form.message}
                                    onChange={set("message")}
                                    maxLength={1000}
                                    rows={3}
                                    className={`${inputCls} resize-y`}
                                />
                            </div>
                            {/* Honeypot — invisible to humans, bots fill it */}
                            <div className="hidden" aria-hidden="true">
                                <label htmlFor="scan-website">Website</label>
                                <input
                                    id="scan-website"
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.website}
                                    onChange={set("website")}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="scan-submit-button"
                            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-orange-dark sm:text-base"
                        >
                            Business Scan
                        </button>

                        <p className="mt-4 text-center text-xs leading-relaxed text-brand-muted">
                            Submitting opens WhatsApp with your details pre-filled — nothing is stored on this site.
                        </p>

                        {status.note && (
                            <p
                                role="status"
                                data-testid="scan-form-status"
                                className={`mt-4 rounded-xl px-4 py-3 text-center text-sm font-medium ${
                                    status.state === "sent"
                                        ? "bg-brand-orange-tint text-brand-brown"
                                        : "bg-brand-cream text-brand-orange-dark"
                                }`}
                            >
                                {status.note}{" "}
                                {status.state !== "sent" && (
                                    <a
                                        href={waLink(WA_MESSAGES.scan)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="scan-fallback-whatsapp-link"
                                        className="font-semibold underline underline-offset-2"
                                    >
                                        Open WhatsApp
                                    </a>
                                )}
                            </p>
                        )}
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
